import { Injectable } from '@angular/core';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { deleteToken, getMessaging, getToken, isSupported, Messaging } from 'firebase/messaging';
import { lastValueFrom } from 'rxjs';
import { firebaseConfig } from 'src/environments/environment';
import { MessageHttpService } from '../message/message.http.service';
import { MessageService } from '../message/message.service';

export const BROWSER_PUSH_SERVICE_WORKER_PATH = '/firebase-messaging-sw.js';
export const BROWSER_PUSH_ALERT_MESSAGE_TYPE = 'BACKOFFICE_ALERT';
const BROWSER_PUSH_TOKEN_STORAGE_KEY = 'browserPushToken';

@Injectable({
  providedIn: 'root',
})
export class BrowserPushService {
  private serviceWorkerListenerRegistered = false;

  constructor(
    private messageHttpService: MessageHttpService,
    private messageService: MessageService,
  ) {}

  async enable(): Promise<void> {
    try {
      if (!(await this.isBrowserPushAvailable())) return;
      if (Notification.permission === 'default') await Notification.requestPermission();
      if (Notification.permission !== 'granted') return;

      const serviceWorkerRegistration = await navigator.serviceWorker.register(BROWSER_PUSH_SERVICE_WORKER_PATH);
      const token = await getToken(this.getFirebaseMessaging(), { serviceWorkerRegistration });
      if (!token) return;

      await lastValueFrom(this.messageHttpService.registerWebPushToken(token));
      localStorage.setItem(BROWSER_PUSH_TOKEN_STORAGE_KEY, token);
      this.listenToServiceWorker();
    } catch (error) {
      console.error('Browser push notifications could not be enabled:', error);
    }
  }

  async disable(): Promise<void> {
    const token = localStorage.getItem(BROWSER_PUSH_TOKEN_STORAGE_KEY);
    if (!token) return;
    localStorage.removeItem(BROWSER_PUSH_TOKEN_STORAGE_KEY);
    try {
      await lastValueFrom(this.messageHttpService.unregisterWebPushToken(token));
    } catch (error) {
      console.error('Browser push token could not be unregistered:', error);
    }
    try {
      if (await this.isBrowserPushAvailable()) await deleteToken(this.getFirebaseMessaging());
    } catch (error) {
      console.error('Browser push token could not be deleted:', error);
    }
  }

  private async isBrowserPushAvailable(): Promise<boolean> {
    return 'serviceWorker' in navigator && 'Notification' in window && (await isSupported());
  }

  private getFirebaseMessaging(): Messaging {
    const firebaseApp = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
    return getMessaging(firebaseApp);
  }

  private listenToServiceWorker() {
    if (this.serviceWorkerListenerRegistered) return;
    this.serviceWorkerListenerRegistered = true;
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      if (event.data?.type === BROWSER_PUSH_ALERT_MESSAGE_TYPE) this.messageService.fetchPushNotifications();
    });
  }
}
